import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BlobType, FileModel, ModuleCategoryV1Read, ModuleUpdate, ModuleV1Read, OptionItem, UploadFileItem, VersionReadModel } from '@ca/core/models';
import { UserNotificationService } from '@ca/core/services';
import { CustomValidators } from '@ca/utils';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Guid } from 'guid-typescript';
import { MODULE_TITLE_PATTERN } from '../../constants/constants';
import { DESCRIPTION_LENGTH_LIMIT, SUMMARY_LENGTH_LIMIT, SUPPORTED_IMAGE_TYPES, TITLE_LENGTH_LIMIT } from '../../constants/form-validators';
import { EditorModalFunctions } from '../module-detail/module-detail.component';

@Component({
    selector: 'ca-module-editor',
    templateUrl: './module-editor.component.html',
    styleUrls: ['./module-editor.component.scss'],
})
export class ModuleEditorComponent implements OnInit {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    @Input() close: (result: string) => void;
    @Input() isEditingAsAdmin: boolean = false;
    @Input() categories: ModuleCategoryV1Read[] = [];
    @Input() compliances: OptionItem<string>[] = [];
    imageUrl: string | null;
    versionUnderEdit: VersionReadModel;
    isEditingLatestPublicVersion: boolean = false;
    moduleImageBlobType: BlobType = BlobType.ModuleImage;
    module: ModuleV1Read = new ModuleV1Read();
    formGroup: UntypedFormGroup;
    functions: EditorModalFunctions;
    isSubmitting: boolean = false;
    titleLengthLimit: number = TITLE_LENGTH_LIMIT;

    readonly descriptionLengthLimit: number = DESCRIPTION_LENGTH_LIMIT;
    readonly summaryLengthLimit: number = SUMMARY_LENGTH_LIMIT;
    readonly supportedImageTypes: string[] = SUPPORTED_IMAGE_TYPES;

    get description(): AbstractControl {
        return this.formGroup.get('description') as AbstractControl;
    }

    constructor(
        private activeModal: NgbActiveModal,
        private formBuilder: UntypedFormBuilder,
        private userNotificationService: UserNotificationService,
    ) {}

    ngOnInit(): void {
        this.isEditingLatestPublicVersion = this.isEditingAsAdmin && !!this.module.latestPublicVersion;
        this.versionUnderEdit = this.isEditingLatestPublicVersion ? this.module.latestPublicVersion! : this.module.draftVersion!;

        const isCompliant: boolean =
            this.module.latestPublicVersion != null &&
            this.module.latestPublicVersion.compliances != null &&
            this.module.latestPublicVersion.compliances.length > 0;
        this.imageUrl = this.versionUnderEdit.image;

        this.formGroup = this.formBuilder.group({
            title: [this.module.title, Validators.pattern(MODULE_TITLE_PATTERN)],
            description: [
                this.versionUnderEdit.description,
                [CustomValidators.requiredWithoutHtmlTags(), CustomValidators.maxLengthWithoutHtmlTags(DESCRIPTION_LENGTH_LIMIT)],
            ],
            summary: [this.versionUnderEdit.summary, CustomValidators.SummaryValidation()],
            imageUrl: [this.versionUnderEdit.image],
            categoryId: [this.module.categoryId ? this.module.categoryId : '', Validators.required],
            isCompliant: [isCompliant, Validators.required],
            ...(this.isEditingAsAdmin && this.module.latestPublicVersion ? { isFeatured: this.module.isFeatured } : {}),
        });
    }

    onUploadCompleted({ token }: FileModel): void {
        const imageUrl = token + '?refresh=' + Guid.create();
        this.formGroup.get('imageUrl')?.setValue(imageUrl);
        this.formGroup.get('imageUrl')?.markAsDirty({ onlySelf: true });
    }

    onTouched(): void {
        this.description.markAsTouched();
    }

    submit(): void {
        if (this.formGroup.valid) {
            const formData = this.formGroup.value;
            const compliances: string[] = [];

            if (this.formGroup.value.isCompliant === true) {
                compliances.push(this.compliances[0].value);
            }

            const moduleUpdateModel: ModuleUpdate = {
                id: this.module.id,
                moduleUpdates: {
                    title: formData.title.trim(),
                    description: formData.description,
                    summary: formData.summary,
                    categoryId: formData.categoryId,
                    image: formData.imageUrl,
                    compliances,
                    isFeatured: formData.isFeatured,
                },
            };

            if (this.isEditingLatestPublicVersion) {
                this.functions?.updateLatestPublicVersion(moduleUpdateModel);
            } else {
                this.functions?.updateModule(moduleUpdateModel);
            }

            this.userNotificationService.showSpinner();
            this.isSubmitting = true;
        } else {
            this.formGroup.markAllAsTouched();
        }
    }

    removeFileItem(fileItem): void {
        if (fileItem) {
            if (fileItem.remove) {
                fileItem.remove();
            } else {
                fileItem.parentNode.removeChild(fileItem);
            }

            this.imageUrl = null;
            this.formGroup.get('imageUrl')?.setValue(null);
        }
    }

    handleStartUpload(): void {
        const existFileItem = document.querySelector('.exist-item');
        this.removeFileItem(existFileItem);
    }

    dismiss(reason?: string): void {
        this.activeModal.dismiss(reason);
    }

    updateImageUrlState(invalidFiles: UploadFileItem[]): void {
        const imgControl: UntypedFormControl | undefined = this.formGroup.get('imageUrl') as UntypedFormControl | undefined;
        if (imgControl != null) {
            this.formGroup.get('imageUrl')?.setValue('');
            this.formGroup.get('imageUrl')?.markAsDirty({ onlySelf: true });
            if (invalidFiles[0].unsupportedFileTypeError) {
                this.formGroup.get('imageUrl')?.setErrors({ invalidImageType: 'Invalid image type.' });
            }
            if (invalidFiles[0].maxFileSizeError) {
                this.formGroup.get('imageUrl')?.setErrors({ maxFileSize: 'File size could not be larger than 1MB.' });
            }
        }
    }
}
